import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories, fetchSubcategories } from '../../redux/actions/CategoriesAction';
import { fetchProductionCompany } from '../../redux/actions/ProductionCompanyAction';
import { fetchFilteredProducts } from '../../redux/actions/filterAction';
import { fetchProductOffers } from '../../redux/actions/ProductsOfferActions';
import { setCompany, setPriceRange, setSort, setSubcategory, setLengthRange, setWeightRange, setSearchFilter } from '../../redux/slices/filterSlice';
import { useTranslation } from 'react-i18next';
import './Offers.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../../App';
import { addToCart, fetchCartItems } from '../../redux/actions/cartAction';
import { toast } from 'react-toastify';

function Offers() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const lang = i18n.language || localStorage.getItem("lang") || "ar";
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [openCategory, setOpenCategory] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [useFilteredData, setUseFilteredData] = useState(false);
    const [priceFrom, setPriceFrom] = useState("");
    const [priceTo, setPriceTo] = useState("");
    const [fromValue, setFromValue] = useState("");
    const [toValue, setToValue] = useState("");
    const [fromLength, setFromLength] = useState("");
    const [toLength, setToLength] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [quantities, setQuantities] = useState({});
    const filters = useSelector((state) => state.filters);
    const categories = useSelector((state) => state.categories.categories);
    const subcategories = useSelector((state) => state.categories.subcategories);
    const companies = useSelector((state) => state.productionCompany.data);
    const offers = useSelector((state) => state.offers.offers);
    const productsData = useSelector((state) => state.products2.data);
    const isLoading = useSelector((state) => state.categories.isLoadingProducts);
    const error = useSelector((state) => state.categories.error);


    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(fetchCategories(lang));
        dispatch(fetchProductionCompany(lang));
        dispatch(fetchProductOffers({ lang, page: currentPage }));
    }, [dispatch, lang, currentPage]);

    useEffect(() => {
        if (selectedCategory) {
            dispatch(fetchSubcategories({ categoryId: selectedCategory, lang }));
        }
    }, [dispatch, selectedCategory, lang]);

    const handleCategoryToggle = useCallback((categoryId) => {
        setOpenCategory(prev => prev === categoryId ? null : categoryId);
        setSelectedCategory(categoryId);
    }, []);
    const handleSubcategoryClick = useCallback((subcategoryId) => {
        if (selectedSubcategory === subcategoryId) {
            setSelectedSubcategory(null);
            dispatch(setSubcategory(null));
            dispatch(fetchFilteredProducts({ ...filters, subcategory: null, lang }));
            setUseFilteredData(true);
        } else {
            setSelectedSubcategory(subcategoryId);
            dispatch(setSubcategory(subcategoryId));
            dispatch(fetchFilteredProducts({ ...filters, subcategory: subcategoryId, lang }));
            setUseFilteredData(true);
        }
    }, [selectedSubcategory, dispatch, filters, lang]);
    const areAllFiltersEmpty = useCallback(() => {
        return !priceFrom && !priceTo && !fromValue && !toValue && !fromLength && !toLength && (!search || search.length <= 1);
    }, [priceFrom, priceTo, fromValue, toValue, fromLength, toLength, search]);
    
    const getActiveFilters = useCallback(() => {
        return {
            lang,
            ...(priceFrom && { price_min: priceFrom }),
            ...(priceTo && { price_max: priceTo }),
            ...(fromValue && { weight_min: fromValue }),
            ...(toValue && { weight_max: toValue }),
            ...(fromLength && { length_min: fromLength }),
            ...(toLength && { length_max: toLength }),
            ...(search && search.length > 1 && { search: search }),
            ...(filters.sort_by && { sort_by: filters.sort_by }),
        };
    }, [lang, priceFrom, priceTo, fromValue, toValue, fromLength, toLength, search]);
    
    const isFiltering = useFilteredData || search?.length > 1 || priceFrom || priceTo || fromValue || toValue || fromLength || toLength || selectedSubcategory || filters?.company || filters?.sort_by;
    
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
        
        if (useFilteredData || isFiltering) {
            const activeFilters = getActiveFilters();
            dispatch(fetchFilteredProducts({ ...activeFilters, page }));
        } else {
            dispatch(fetchProductOffers({ lang, page }));
        }
    }, [useFilteredData, isFiltering, getActiveFilters, dispatch, lang]);
    const handleApplyPrice = () => {
        setCurrentPage(1);
        
        if (areAllFiltersEmpty()) {
            setUseFilteredData(false);
            dispatch(setPriceRange({ min: '', max: '' }));
            dispatch(fetchProductOffers({ lang, page: 1 }));
            window.scrollTo(0, 0);
        } else {
            const activeFilters = getActiveFilters();
            dispatch(setPriceRange({ min: priceFrom, max: priceTo }));
            setTimeout(() => {
                dispatch(fetchFilteredProducts({ ...activeFilters, page: 1 }));
                setUseFilteredData(true);
                window.scrollTo(0, 0);
            }, 0);
        }
    };
    const handleApplyWeight = () => {
        setCurrentPage(1);
        
        if (areAllFiltersEmpty()) {
            setUseFilteredData(false);
            dispatch(setWeightRange({ min: '', max: '' }));
            dispatch(fetchProductOffers({ lang, page: 1 }));
            window.scrollTo(0, 0);
        } else {
            dispatch(setWeightRange({ min: fromValue, max: toValue }));
            setTimeout(() => {
                dispatch(fetchFilteredProducts({ ...getActiveFilters(), page: 1 }));
                setUseFilteredData(true);
                window.scrollTo(0, 0);
            }, 0);
        }
    };
    const handleApplyLength = () => {
        setCurrentPage(1);
        
        if (areAllFiltersEmpty()) {
            setUseFilteredData(false);
            dispatch(setLengthRange({ min: '', max: '' }));
            dispatch(fetchProductOffers({ lang, page: 1 }));
            window.scrollTo(0, 0);
        } else {
            dispatch(setLengthRange({ min: fromLength, max: toLength }));
            setTimeout(() => {
                dispatch(fetchFilteredProducts({ ...getActiveFilters(), page: 1 }));
                setUseFilteredData(true);
                window.scrollTo(0, 0);
            }, 0);
        }
    };
    const handleCompanyClick = (companyId) => {
        setCurrentPage(1);
        dispatch(setCompany(companyId));
        setTimeout(() => {
            const filters = getActiveFilters();
            filters.company = companyId;
            dispatch(fetchFilteredProducts({ ...filters, page: 1 }));
            setUseFilteredData(true);
            window.scrollTo(0, 0);
        }, 0);
    };
    const handleSortChange = (value) => {
        setCurrentPage(1);
        dispatch(setSort(value));
        setTimeout(() => {
            const currentFilters = getActiveFilters();
            currentFilters.sort_by = value;
            dispatch(fetchFilteredProducts({ ...currentFilters, page: 1 }));
            setUseFilteredData(true);
            window.scrollTo(0, 0);
        }, 0);
    };

    const handleQuantityChange = (product, delta) => {
        const step = product.count || 1;
        setQuantities(prev => {
            const current = prev[product.id] || step;
            return {
               ...prev,
               [product.id]: Math.max(step, current + (delta * step))
            };
        });
    };

    const getProductQuantity = (product) => {
        return quantities[product.id] || product.count || 1;
    };
    useEffect(() => {
        if (search && search.length > 1) {
            dispatch(setSearchFilter(search));
            dispatch(fetchFilteredProducts({ search: search, lang, page: currentPage }));
            setUseFilteredData(true);
        } else if (search === "") {
            setUseFilteredData(false);
            const params = new URLSearchParams(location.search);
            if (params.has('search')) {
                params.delete('search');
                navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
            }
        }
    }, [search, dispatch, lang, location, navigate, currentPage]);

    const handleSearch = (e) => {
        e.preventDefault();
    };
    const subcatList = Array.isArray(subcategories[String(selectedCategory)])
        ? subcategories[String(selectedCategory)]
        : subcategories[String(selectedCategory)]?.data || [];
    const currentData = useFilteredData
        ? { data: productsData }
        : { data: offers };
    const allProducts = useSelector((state) => state.offers.offers);
    
    const offersPagination = useSelector((state) => state.offers.pagination);
    const productsPagination = useSelector((state) => state.products2.pagination);
    
    const { currentPagination, totalPages } = useMemo(() => {
        const pagination = useFilteredData ? productsPagination : offersPagination;
        const totalPages = pagination?.last_page || 1;
        return { currentPagination: pagination, totalPages };
    }, [useFilteredData, productsPagination, offersPagination]);

    const productsArray = useMemo(() => {
        if (isFiltering) {
            if (Array.isArray(productsData)) {
                return productsData;
            }
            if (productsData && Array.isArray(productsData.data)) {
                return productsData.data;
            }
            return [];
        } else {
            if (Array.isArray(allProducts)) {
                return allProducts;
            }
            if (allProducts && Array.isArray(allProducts.data)) {
                return allProducts.data;
            }
            return [];
        }
    }, [isFiltering, productsData, allProducts]);

    return (
        <div className={`offers-section container-fluid my-4 ${isDarkMode ? 'dark-mode' : ''}`} style={{ background: isDarkMode ? '#0a0e27' : 'transparent' }}>
            <div className={`breadcrumb-box text-center py-5 mb-4 rounded ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                background: isDarkMode ? '#0a0e27' : "#EF7F030D",
                border: isDarkMode ? '1px solid #2d3748' : 'none'
            }}>
                <h4 className="fw-bold" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d' }}>
                    {t('navbar.offers')}
                </h4>
                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                    {i18n.language === 'ar' ? 'الرئيسية / عروضنا' : 'Home / Offers'}
                </p>
            </div>
            <div className="row">
                
                <div className="col-lg-12 col-md-12">

                    {isLoading ? (
                        <div className="row g-4">
                            {[...Array(6)].map((_, idx) => (
                                <div className="col-sm-6 col-lg-4" key={idx}>
                                    <div className="product-card text-center p-3 rounded shadow-sm bg-white position-relative">
                                        <div style={{ width: '100%', height: 160, background: '#eee', borderRadius: 8, marginBottom: 16, animation: 'skeleton 1.2s infinite linear alternate' }} />
                                        <div style={{ width: '60%', height: 18, background: '#e0e0e0', borderRadius: 4, margin: '0 auto 8px', animation: 'skeleton 1.2s infinite linear alternate' }} />
                                        <div style={{ width: '40%', height: 14, background: '#e0e0e0', borderRadius: 4, margin: '0 auto 8px', animation: 'skeleton 1.2s infinite linear alternate' }} />
                                        <div style={{ width: '30%', height: 16, background: '#e0e0e0', borderRadius: 4, margin: '0 auto 12px', animation: 'skeleton 1.2s infinite linear alternate' }} />
                                        <div style={{ width: '60%', height: 24, background: '#f5f5f5', borderRadius: 12, margin: '0 auto', animation: 'skeleton 1.2s infinite linear alternate' }} />
                                    </div>
                                </div>
                            ))}
                            <style>{`
                                @keyframes skeleton {
                                    0% { background-color: #eee; }
                                    100% { background-color: #f5f5f5; }
                                }
                            `}</style>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : Array.isArray(productsArray) && productsArray.length === 0 ? (
                        <div className="col-12 text-center">
                            <div 
                                className={`empty-state rounded-4 p-5 mx-auto ${isDarkMode ? 'dark-mode' : ''}`}
                                style={{
                                    background: isDarkMode ? '#0a0e27' : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    border: isDarkMode ? '1px solid var(--color-border)' : '1px solid rgba(0,12,61,0.08)',
                                    boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.05)',
                                    maxWidth: '500px'
                                }}
                            >
                                <div className="mb-4">
                                    <i className="fas fa-box-open text-muted" style={{ fontSize: '4rem', color: isDarkMode ? 'var(--color-muted)' : '#6c757d' }}></i>
                                </div>
                                <h5 className="fw-bold mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d', fontSize: '1.5rem' }}>
                                    {t('store.noProducts')}
                                </h5>
                                <p className="text-muted mb-4" style={{ fontSize: '1rem', color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                    {t('store.tryDifferentFilters', 'جرب فلتر مختلف أو ابحث عن شيء آخر')}
                                </p>
                                <button 
                                    className="btn btn-primary rounded-pill px-4 py-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #000c3d 0%, #1a237e 100%)',
                                        border: 'none',
                                        fontWeight: '600'
                                    }}
                                    onClick={() => {
                                        setSearch('');
                                        setUseFilteredData(false);
                                    }}
                                >
                                    <i className="fas fa-refresh me-2"></i>
                                    {t('store.resetFilters', 'إعادة تعيين الفلاتر')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(600px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
                            {Array.isArray(productsArray) && productsArray.map((product) => (
                                <div 
                                    key={product.id} 
                                    style={{
                                        background: isDarkMode ? 'var(--color-card)' : 'white',
                                        borderRadius: '15px',
                                        overflow: 'hidden',
                                        boxShadow: isDarkMode ? '0 5px 15px rgba(0,0,0,0.2)' : '0 5px 15px rgba(0,0,0,0.1)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transform: 'translateY(0)',
                                        border: isDarkMode ? '1px solid var(--color-border)' : '1px solid rgba(0,0,0,0.05)',
                                        display: 'flex',
                                        flexDirection: 'row'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-10px)';
                                        e.currentTarget.style.boxShadow = isDarkMode 
                                            ? '0 15px 30px rgba(0,0,0,0.3)' 
                                            : '0 15px 30px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = isDarkMode 
                                            ? '0 5px 15px rgba(0,0,0,0.2)' 
                                            : '0 5px 15px rgba(0,0,0,0.1)';
                                    }}
                                    onClick={() => navigate(`/productDetails/${product.id}`)}
                                >
                                    {/* Product Image */}
                                    <div style={{ height: '200px', width: '200px', minWidth: '200px', overflow: 'hidden', position: 'relative' }}>
                                        <img
                                            src={product.images && product.images.length > 0 ? product.images[0] : "/Frame.png"}
                                            alt={product.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s ease'
                                            }}
                                            onError={(e) => {
                                                e.target.src = "/Frame.png";
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'scale(1.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                        />
                                        {product.offer_percent && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                left: '10px',
                                                background: '#dc3545',
                                                color: 'white',
                                                padding: '5px 12px',
                                                borderRadius: '8px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                boxShadow: '0 2px 10px rgba(220,53,69,0.3)'
                                            }}>
                                                {product.offer_percent}%
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Product Info */}
                                    <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1, minHeight: '200px' }}>
                                        <h3 style={{ 
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            marginBottom: '0.5rem',
                                            color: isDarkMode ? 'var(--color-text)' : '#333',
                                            lineHeight: '1.4',
                                            minHeight: '2.8rem',
                                            display: 'flex',
                                            alignItems: 'flex-start'
                                        }}>
                                            {product.name}
                                        </h3>
                                        {/* Product Code */}
                                        {product.code && (
                                            <div style={{ color: isDarkMode ? 'var(--color-muted)' : '#6c757d', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                                {t('product.code')}: {product.code}
                                            </div>
                                        )}
                                        
                                        <div 
                                            style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginTop: 'auto',
                                                paddingTop: '0.75rem',
                                                borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f0f0f0'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            {/* Actions Side (Left in RTL) */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {/* Add to Cart Button */}
                                                <button
                                                    style={{ 
                                                        width: '38px', 
                                                        height: '38px',
                                                        background: 'linear-gradient(135deg, #ff3e3e 0%, #ff1a1a 100%)',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 10px rgba(255, 62, 62, 0.3)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        dispatch(addToCart({ 
                                                            product_id: product.id, 
                                                            qty: getProductQuantity(product), 
                                                            language: i18n.language 
                                                        })).unwrap()
                                                        .then(() => {
                                                            toast.success(t('cart.addedSuccessfully') || "تم إضافة المنتج للسلة بنجاح");
                                                            dispatch(fetchCartItems(i18n.language));
                                                            setQuantities(prev => ({ ...prev, [product.id]: product.count || 1 }));
                                                        })
                                                        .catch((err) => toast(err.message || t('cart.addError')));
                                                    }}
                                                >
                                                    <i className="fas fa-shopping-cart" style={{ color: 'white', fontSize: '0.85rem' }}></i>
                                                </button>

                                                {/* Quantity Box */}
                                                <div style={{ 
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'white',
                                                    borderRadius: '20px',
                                                    padding: '0.25rem 0.5rem',
                                                    border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #eee',
                                                    height: '32px',
                                                    boxShadow: isDarkMode ? 'none' : '0 2px 5px rgba(0,0,0,0.05)'
                                                }}>
                                                    <button 
                                                        style={{
                                                            width: '22px',
                                                            height: '22px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#ff3e3e',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            fontSize: '10px'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuantityChange(product, 1);
                                                        }}
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                    <span style={{
                                                        margin: '0 0.5rem',
                                                        fontWeight: 'bold',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                        fontSize: '14px',
                                                        color: isDarkMode ? 'white' : '#333'
                                                    }}>
                                                        {getProductQuantity(product)}
                                                    </span>
                                                    <button 
                                                        style={{
                                                            width: '22px',
                                                            height: '22px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#ff3e3e',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            fontSize: '10px'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuantityChange(product, -1);
                                                        }}
                                                    >
                                                        <i className="fas fa-minus"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Price Side (Right in RTL) */}
                                            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                {product.offer_type === 'fixed' && product.final_price > 0 ? (
                                                    <>
                                                        <span style={{ 
                                                            color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                            fontSize: '1.2rem',
                                                            fontWeight: '600',
                                                            lineHeight: '1.2'
                                                        }}>
                                                            {product.final_price} <span style={{ fontSize: '0.9rem' }}>{t("cart.currency")}</span>
                                                        </span>
                                                        <span style={{ textDecoration: 'line-through', color: isDarkMode ? 'var(--color-muted)' : '#999', fontSize: '0.8rem' }}>
                                                            {product.price}
                                                        </span>
                                                    </>
                                                ) : product.offer_percent > 0 && product.final_price > 0 ? (
                                                    <>
                                                        <span style={{ 
                                                            color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                            fontSize: '1.2rem',
                                                            fontWeight: '600',
                                                            lineHeight: '1.2'
                                                        }}>
                                                            {product.final_price} <span style={{ fontSize: '0.9rem' }}>{t("cart.currency")}</span>
                                                        </span>
                                                        <span style={{ textDecoration: 'line-through', color: isDarkMode ? 'var(--color-muted)' : '#999', fontSize: '0.8rem' }}>
                                                            {product.price}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span style={{ 
                                                        color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                        fontSize: '1.2rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        {product.price} <span style={{ fontSize: '0.9rem' }}>{t("cart.currency")}</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Collection Style Pagination */}
                        {currentPagination && currentPagination.total > 1 && (
                            <div
                                className="text-center mt-5"
                                style={{ opacity: 1 }}
                            >
                                {Array.from({ length: currentPagination.last_page }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handlePageChange(i + 1)}
                                        style={{
                                            backgroundColor: currentPagination.current_page === i + 1 
                                                ? (isDarkMode ? 'var(--color-primary)' : "#000c3d")
                                                : (isDarkMode ? 'var(--color-card)' : "transparent"),
                                            color: currentPagination.current_page === i + 1 
                                                ? "#fff" 
                                                : (isDarkMode ? 'var(--color-text)' : "#000c3d"),
                                            border: isDarkMode ? "1px solid var(--color-primary)" : "1px solid #000c3d",
                                            borderRadius: "10px",
                                            padding: "5px 12px",
                                            margin: "0 5px",
                                            cursor: "pointer"
                                        }}
                                        className={`btn mx-1 ${currentPagination.current_page === i + 1
                                            ? ""
                                            : "btn-outline-primary"
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                        
                        {/* Results Info */}
                        {currentPagination && (
                            <div className="text-center mt-4">
                                <p className="text-muted" style={{ fontSize: '0.9rem', color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                    {t('home.showingResults', { 
                                        from: ((currentPage - 1) * currentPagination.per_page) + 1,
                                        to: Math.min(currentPage * currentPagination.per_page, currentPagination.total),
                                        total: currentPagination.total
                                    })}
                                </p>
                            </div>
                        )}
                        </>
                    )}
                </div>
            </div>

            {/* Custom CSS for Offers */}
            <style jsx="true">{`
                @keyframes skeleton {
                    0% { background-color: #eee; }
                    100% { background-color: #f5f5f5; }
                }
            `}</style>
        </div>
    );
}

export default Offers;
